import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Star, CheckCircle } from "@phosphor-icons/react";
import { api, errorMessage } from "@/lib/api";

export default function ReviewPage() {
  const { token } = useParams();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["review-invite", token],
    queryFn: async () => (await api.get(`/review/${token}`)).data,
    retry: false,
  });
  const [form, setForm] = useState({ rating: 0, body: "", name: "", city: "", favourite_poem_id: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) return setErr("Please pick a star rating");
    setBusy(true); setErr("");
    try {
      await api.post(`/review/${token}`, { ...form, name: form.name || data.customer_name });
      setDone(true);
    } catch (e2) { setErr(errorMessage(e2)); } finally { setBusy(false); }
  };

  if (isLoading) return <p className="mx-auto max-w-xl px-5 py-24 text-sm text-muted-foreground">Loading…</p>;
  if (isError) return <p className="mx-auto max-w-xl px-5 py-24 text-base" data-testid="review-invalid">{errorMessage(error, "This review link is not valid any more.")}</p>;

  if (done || data.already_reviewed) {
    return (
      <div className="mx-auto max-w-xl px-5 py-24 text-center" data-testid="review-thanks">
        <CheckCircle size={40} weight="duotone" className="mx-auto text-primary" />
        <h1 className="mt-5 font-display text-3xl">Thank you.</h1>
        <p className="mt-3 text-base text-foreground/75">Your words will appear on the site once Shunmugavel has read them.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-5 py-16 sm:px-8 md:py-24" data-testid="review-page">
      <p className="eyebrow">A note from you</p>
      <h1 className="mt-3 font-display text-3xl leading-tight sm:text-4xl">How were the poems, {data.customer_name?.split(" ")[0]}?</h1>
      <p className="mt-4 text-base leading-relaxed text-foreground/75">
        A line or two is plenty. Your words help other readers find {data.book?.title}.
      </p>

      <form onSubmit={submit} className="mt-10 space-y-7">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Your rating</p>
          <div className="flex gap-1.5" data-testid="rating-picker">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })} aria-label={`${n} star`} data-testid={`rating-star-${n}`}>
                <motion.span whileHover={{ scale: 1.12 }} className="block">
                  <Star size={34} weight={form.rating >= n ? "fill" : "regular"} className={form.rating >= n ? "text-primary" : "text-foreground/25"} />
                </motion.span>
              </button>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Your words</span>
          <textarea required rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder="What stayed with you?"
            className="w-full rounded-sm border border-border bg-white px-4 py-3 text-base outline-none transition-colors focus:border-primary"
            data-testid="review-body-input" />
        </label>

        {data.poems?.length > 0 && (
          <label className="block">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Favourite poem (optional)</span>
            <select value={form.favourite_poem_id} onChange={(e) => setForm({ ...form, favourite_poem_id: e.target.value })}
              className="w-full rounded-sm border border-border bg-white px-4 py-3 text-base outline-none focus:border-primary" data-testid="favourite-poem-select">
              <option value="">No favourite — all of them</option>
              {data.poems.map((p) => <option key={p.poem_id} value={p.poem_id}>{p.title_ta}{p.title_en ? ` · ${p.title_en}` : ""}</option>)}
            </select>
          </label>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Name shown</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={data.customer_name}
              className="w-full rounded-sm border border-border bg-white px-4 py-3 text-base outline-none focus:border-primary" data-testid="review-name-input" />
          </label>
          <label className="block">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">City (optional)</span>
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Coimbatore"
              className="w-full rounded-sm border border-border bg-white px-4 py-3 text-base outline-none focus:border-primary" data-testid="review-city-input" />
          </label>
        </div>

        {err && <p className="text-sm font-medium text-destructive" data-testid="review-error">{err}</p>}

        <button type="submit" disabled={busy} className="rounded-sm bg-primary px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors duration-200 hover:bg-[#702F2A] disabled:opacity-50" data-testid="review-submit-button">
          {busy ? "Sending…" : "Send my review"}
        </button>
        <p className="text-xs text-muted-foreground">Shown as “{form.name || data.customer_name}{form.city ? ` · ${form.city}` : ""}” with a verified-buyer mark, once approved.</p>
      </form>
    </div>
  );
}
